import { useState, useEffect } from "react";
import { Spin, List, Tag, Button, Input, Select, Space, Tabs, message, Drawer, Tooltip, Typography } from 'antd';
import { ScheduleOutlined, FileAddOutlined, CarryOutTwoTone, UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { startGetTareasOp, startTerminarTarea, startGetUsuariosTareas, startCrearTarea } from "../../../../../context/actions/tareasOpActions";
import { formatTareaDescription } from "../../../../../utils/fechaUtils";

export default function DrawerTareasOp({ visible, onClose, ordenPagoID, onTareasChange }) {
    const dispatch = useDispatch()
    const { ordenPago } = useSelector((state) => state.ordenesPagoReducer)
    const [tareas, setTareas] = useState([])
    const [loading, setLoading] = useState(false)
    const [usuarios, setUsuarios] = useState([])
    const [descripcion, setDescripcion] = useState('')
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null)
    const [loadingCrear, setLoadingCrear] = useState(false)
    const [tabActivo, setTabActivo] = useState('todas')

    const fetchTareas = () => {
        setLoading(true)
        dispatch(startGetTareasOp(ordenPagoID))
            .then(result => {
                if (result.success) {
                    setTareas(result.data)
                    if (onTareasChange) {
                        const enProceso = result.data.filter(t => !t.fechaConcluido).length
                        onTareasChange(enProceso)
                    }
                }
            })
            .catch(() => setTareas([]))
            .finally(() => setLoading(false))
    }

    const handleTerminar = (tareaOpID) => {
        dispatch(startTerminarTarea(tareaOpID))
            .then(result => {
                if (result.success) {
                    message.success('Tarea concluida')
                    fetchTareas()
                } else {
                    message.error(result.error || 'Error al terminar la tarea')
                }
            })
    }

    const handleCrear = () => {
        if (!descripcion.trim()) return
        setLoadingCrear(true)
        dispatch(startCrearTarea({
            ordenPagoID: Number(ordenPagoID),
            descripcion,
            usuarioIDAsignado: usuarioSeleccionado
        }))
            .then(result => {
                if (result.success) {
                    message.success('Tarea creada')
                    setDescripcion('')
                    setUsuarioSeleccionado(null)
                    fetchTareas()
                } else {
                    message.error(result.error || 'Error al crear tarea')
                }
            })
            .finally(() => setLoadingCrear(false))
    }

    const tareasOrdenadas = [...tareas].sort((a, b) => {
        if (!a.fechaConcluido && b.fechaConcluido) return -1
        if (a.fechaConcluido && !b.fechaConcluido) return 1
        if (a.fechaConcluido && b.fechaConcluido) return new Date(b.fechaConcluido) - new Date(a.fechaConcluido)
        return new Date(b.fechaInicio) - new Date(a.fechaInicio)
    })

    const enProceso = tareas.filter(t => !t.fechaConcluido).length
    const terminadas = tareas.filter(t => t.fechaConcluido).length

    const tareasFiltradas = tabActivo === 'en_proceso'
        ? tareasOrdenadas.filter(t => !t.fechaConcluido)
        : tabActivo === 'terminadas'
            ? tareasOrdenadas.filter(t => t.fechaConcluido)
            : tareasOrdenadas

    useEffect(() => {
        if (visible && ordenPagoID) {
            fetchTareas()
            if (usuarios.length === 0) {
                dispatch(startGetUsuariosTareas())
                    .then(result => {
                        if (result.success) setUsuarios(result.data)
                    })
                    .catch(() => setUsuarios([]))
            }
        }
    }, [visible, ordenPagoID])

    return (
        <Drawer
            title={
                <span>
                    <ScheduleOutlined style={{ marginRight: 8 }} />
                    Tareas de la OP: <Typography.Text type="warning">{ordenPagoID}</Typography.Text> | Creada por: <Typography.Text type="warning">{ordenPago?.usuarioCreacion}</Typography.Text>
                </span>
            }
            open={visible}
            onClose={onClose}
            width={600}
            placement="right"
            className="pagos-v2-modal"
        >
            <Spin spinning={loading}>
                <>
                    <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 16 }}>
                        <Input.TextArea
                            placeholder="Descripción de la tarea..."
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            rows={2}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Select
                                placeholder="Asignar a..."
                                value={usuarioSeleccionado}
                                onChange={setUsuarioSeleccionado}
                                style={{ flex: 1 }}
                                allowClear
                                showSearch
                                suffixIcon={<UserOutlined />}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {usuarios.map(u => (
                                    <Select.Option key={u.usuarioID} value={u.usuarioID}>
                                        {u.nombre}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                icon={<FileAddOutlined />}
                                onClick={handleCrear}
                                loading={loadingCrear}
                                disabled={!descripcion.trim() || !usuarioSeleccionado}
                            >
                                Crear tarea
                            </Button>
                        </div>
                    </Space>

                    <Tabs
                        activeKey={tabActivo}
                        onChange={setTabActivo}
                        size="small"
                        style={{ marginBottom: 8 }}
                        items={[
                            { key: 'todas', label: `Todas (${tareas.length})` },
                            { key: 'en_proceso', label: `En proceso (${enProceso})` },
                            { key: 'terminadas', label: `Terminadas (${terminadas})` },
                        ]}
                    />

                    <List
                        dataSource={tareasFiltradas}
                        locale={{ emptyText: 'No hay tareas' }}
                        renderItem={(tarea) => {
                            const { linea1, linea2 } = formatTareaDescription(tarea, usuarios)
                            return (
                            <List.Item
                                actions={[
                                    ...(!tarea.fechaConcluido
                                        ? [<Tooltip title="Marcar como terminada"><Button size="small" icon={<CarryOutTwoTone />} onClick={() => handleTerminar(tarea.id)} /></Tooltip>]
                                        : []
                                    ),
                                    <Tag className="btn-rounded" color={tarea.fechaConcluido ? 'green' : 'orange'}>
                                        {tarea.fechaConcluido ? 'Terminada' : 'En proceso'}
                                    </Tag>
                                ]}
                            >
                                <List.Item.Meta
                                    title={tarea.descripcion}
                                    description={<div style={{ fontSize: 11 }}><div>{linea1}</div><div>{linea2}</div></div>}
                                />
                            </List.Item>
                            )
                        }}
                    />
                </>
            </Spin>
        </Drawer>
    )
}